// PriceFilterForm migrated from Final Form to react-hook-form + yup.

import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import debounce from 'lodash/debounce';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import ControlledRangeSlider from '../../components/BasicUI/ControlledInputs/ControlledRangeSlider';
import ControlledTextField from '../../components/BasicUI/ControlledInputs/ControlledTextField';
import { FormattedMessage } from '../../util/reactIntl';

const DEBOUNCE_WAIT_TIME = 400;

// This filter form has NO validation rules (the original Final-Form form had none).
// We still go through yupResolver to match the project's RHF convention, with a
// permissive schema that always passes and never blocks submit/onChange.
const validationSchema = yup.object({
  minPrice: yup.mixed(),
  maxPrice: yup.mixed(),
});

// Parse value for the min handle: must stay between the slider's min and the current max.
// Returns '' for non-numeric input (mirrors the original parseMin behavior).
const parseMin = (min, currentMax) => (value) => {
  const parsedValue = Number.parseInt(value, 10);
  if (isNaN(parsedValue)) {
    return '';
  }
  return parsedValue < min ? min : parsedValue > currentMax ? currentMax : parsedValue;
};

// Parse value for the max handle: must stay between the current min and the slider's max.
// Returns '' for non-numeric input (mirrors the original parseMax behavior).
const parseMax = (max, currentMin) => (value) => {
  const parsedValue = Number.parseInt(value, 10);
  if (isNaN(parsedValue)) {
    return '';
  }
  return parsedValue < currentMin ? currentMin : parsedValue > max ? max : parsedValue;
};

// Map empty-string fields to the slider min/max bounds, preserving any extra values.
// An empty input means "use the slider min/max bound" (load-bearing for filtering).
const normalizeValues = (values, min, max) => {
  const { minPrice, maxPrice, ...restValues } = values;
  return {
    minPrice: minPrice === '' ? min : minPrice,
    maxPrice: maxPrice === '' ? max : maxPrice,
    ...restValues,
  };
};

const createLabels = (intl) => ({
  clear: intl.formatMessage({ id: 'PriceFilterForm.clear' }),
  cancel: intl.formatMessage({ id: 'PriceFilterForm.cancel' }),
  submit: intl.formatMessage({ id: 'PriceFilterForm.submit' }),
});

// Compact underline-style number input that mirrors the original raw <input>:
// centered text, an attention-coloured bottom border, and NO native spin buttons
// (the spinners otherwise overlap and hide the digits in the small 48px field).
const priceInputSx = {
  width: 72,
  '& .MuiInput-input': {
    textAlign: 'center',
    MozAppearance: 'textfield',
  },
  '& .MuiInput-input::-webkit-outer-spin-button, & .MuiInput-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

const euroAdornment = <InputAdornment position="end">€</InputAdornment>;

// Form root sx, reproducing classNames(css.root, { popup, isOpenAsPopup, plain, isOpen }).
// css.root always applies; modifier objects are layered on in CSS source order.
const formSx = ({ showAsPopup, isOpen }) => (theme) => ({
  // .root
  display: 'none',
  outline: 'none',
  // .popup
  ...(showAsPopup && {
    display: 'block',
    visibility: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: theme.tokens.zIndexPopup,
    padding: '15px 30px 20px 30px',
    marginTop: '7px',
    backgroundColor: theme.tokens.matterColorLight,
    borderTop: `1px solid ${theme.tokens.matterColorNegative}`,
    boxShadow: theme.tokens.boxShadowPopup,
    borderRadius: '4px',
    transition: theme.tokens.transitionStyleButton,
  }),
  // .isOpenAsPopup
  ...(showAsPopup &&
    isOpen && {
      display: 'block',
      visibility: 'visible',
      opacity: 1,
      pointerEvents: 'auto',
    }),
  // .plain
  ...(!showAsPopup && {
    width: '100%',
  }),
  // .isOpen
  ...(!showAsPopup &&
    isOpen && {
      display: 'block',
    }),
});

// .contentWrapper
const contentWrapperSx = (theme) => ({
  display: 'flex',
  flexWrap: 'wrap',
  maxWidth: '300px',
  paddingTop: '5px',
  paddingBottom: '5px',
  marginTop: 0,
  [theme.breakpoints.up('md')]: {
    paddingTop: '7px',
    paddingBottom: '7px',
    marginTop: 0,
  },
});

// .label
const labelSx = (theme) => ({
  ...theme.mixins.marketplaceSearchFilterSublabelFontStyles,
  padding: '8px 0 12px 0',
  marginRight: '18px',
  [theme.breakpoints.up('md')]: {
    padding: '8px 0 16px 0',
  },
});

// .inputsWrapper
const inputsWrapperSx = {
  display: 'flex',
  alignItems: 'center',
};

// .priceSeparator
const priceSeparatorSx = {
  margin: '0 8px',
};

// .sliderWrapper
const sliderWrapperSx = (theme) => ({
  display: 'flex',
  padding: '17px 0 25px 0',
  [theme.breakpoints.up('md')]: {
    padding: '16px 0 24px 0',
  },
});

// .buttonsWrapper
const buttonsWrapperSx = {
  display: 'flex',
};

// .clearButton
const clearButtonSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  color: theme.tokens.matterColorAnti,
  margin: '0 auto 0 0',
  padding: 0,
  outline: 'none',
  border: 'none',
  cursor: 'pointer',
  '&:focus, &:hover': {
    color: theme.tokens.matterColor,
    transition: `width ${theme.tokens.transitionStyleButton}`,
  },
});

// .cancelButton
const cancelButtonSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  color: theme.tokens.matterColorAnti,
  margin: 0,
  padding: 0,
  outline: 'none',
  border: 'none',
  cursor: 'pointer',
  // clearButton will add all available space between cancelButton,
  // but some hard coded margin is still needed
  marginLeft: '48px',
  '&:focus, &:hover': {
    color: theme.tokens.matterColor,
    transition: `width ${theme.tokens.transitionStyleButton}`,
  },
});

// .submitButton
const submitButtonSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  color: theme.tokens.marketplaceColor,
  margin: '0 0 0 19px',
  padding: 0,
  outline: 'none',
  border: 'none',
  cursor: 'pointer',
  '&:focus, &:hover': {
    color: theme.tokens.marketplaceColorDark,
    transition: `width ${theme.tokens.transitionStyleButton}`,
  },
});

/**
 * PriceFilterForm is a search filter form with two output modes controlled by `liveEdit`.
 * - liveEdit=true (PriceFilterPlain): no buttons; fires `onChange` (debounced) on every dirty change.
 * - liveEdit=false (PriceFilterPopup): Clear / Cancel / Submit buttons; fires `onSubmit` on submit.
 *
 * @param {object} props
 * @param {boolean} [props.liveEdit] - Whether to run in live-edit (onChange) mode.
 * @param {Function} [props.contentRef] - Callback ref attached to the form element.
 * @param {Function} [props.onCancel] - Called on cancel (popup mode).
 * @param {Function} [props.onChange] - Called on every dirty change (live-edit mode).
 * @param {Function} [props.onClear] - Called on clear (popup mode).
 * @param {Function} [props.onSubmit] - Called on submit (popup mode).
 * @param {string} [props.id] - Id prefix for the price inputs.
 * @param {object} [props.initialValues] - Initial { minPrice, maxPrice } values.
 * @param {number} [props.min] - Slider minimum bound.
 * @param {number} [props.max] - Slider maximum bound.
 * @param {number} [props.step] - Slider step.
 * @param {boolean} [props.showAsPopup] - Whether the form is rendered as a popup.
 * @param {boolean} [props.isOpen] - Whether the popup/plain container is open.
 * @param {object} [props.style] - Inline style for the form element.
 */
const PriceFilterFormComponent = ({
  liveEdit = false,
  contentRef = null,
  onCancel = () => {},
  onChange = () => {},
  onClear = () => {},
  onSubmit = () => {},
  id,
  initialValues,
  min,
  max,
  step,
  showAsPopup = false,
  isOpen = false,
  style,
}) => {
  const intl = useIntl();

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions',
    );
  }

  const labels = useMemo(() => createLabels(intl), [intl]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  // Current raw field values used to clamp the text inputs and feed the slider handles.
  const minPriceRaw = watch('minPrice');
  const maxPriceRaw = watch('maxPrice');

  // Convert the empty-string state to the bound so the slider handles always get numbers.
  const minPrice = typeof minPriceRaw !== 'string' ? minPriceRaw : min;
  const maxPrice = typeof maxPriceRaw !== 'string' ? maxPriceRaw : max;

  // Keep the latest dirty flag in a ref so the watch subscription reads the value at
  // emit time (mirrors the original FormSpy `formState.dirty` check) instead of a stale
  // closure captured when the subscription was created.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Stable debounced onChange so the timer survives re-renders (fixes a stale-closure /
  // debounce-reset bug: the original recreated debounce() every render via FormSpy).
  const debouncedOnChange = useMemo(
    () =>
      debounce(
        (values) => {
          onChange(normalizeValues(values, min, max));
        },
        DEBOUNCE_WAIT_TIME,
        { leading: false, trailing: true },
      ),
    [onChange, min, max],
  );

  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  // Live-edit mode: fire onChange (debounced) only when the form is dirty.
  useEffect(() => {
    if (!liveEdit) {
      return undefined;
    }
    const subscription = watch((values) => {
      // watch(cb) only emits on actual value changes (never on mount), so we react to
      // every change as long as the form is dirty. This mirrors the original FormSpy
      // `if (formState.dirty)` gate, and covers BOTH the text inputs (setValue) and the
      // slider (field.onChange) — gating on the event `type` would have dropped the
      // setValue-driven text-input changes entirely.
      if (isDirtyRef.current) {
        debouncedOnChange(values);
      }
    });
    return () => subscription.unsubscribe();
  }, [liveEdit, watch, debouncedOnChange]);

  const onValidSubmit = (values) => onSubmit(normalizeValues(values, min, max));

  const handleCancel = () => {
    reset();
    onCancel();
  };

  // Clamp helpers for the two raw number inputs (mirrors the original Field parse).
  const handleMinChange = (event) => {
    setValue('minPrice', parseMin(min, maxPrice)(event.target.value), { shouldDirty: true });
  };
  const handleMaxChange = (event) => {
    setValue('maxPrice', parseMax(max, minPrice)(event.target.value), { shouldDirty: true });
  };

  return (
    <Box
      action="/"
      component="form"
      method="post"
      onSubmit={handleSubmit(onValidSubmit)}
      ref={contentRef}
      style={{ ...style }}
      sx={formSx({ showAsPopup, isOpen })}
      tabIndex="0"
    >
      <Box sx={contentWrapperSx}>
        <Box component="span" sx={labelSx}>
          <FormattedMessage id="PriceFilterForm.label" />
        </Box>
        <Box sx={inputsWrapperSx}>
          <ControlledTextField
            control={control}
            name="minPrice"
            stackProps={{ width: 'auto' }}
            textFieldProps={{
              variant: 'standard',
              id: `${id}.minPrice`,
              inputProps: { max, min, step },
              InputProps: { endAdornment: euroAdornment },
              onChange: handleMinChange,
              placeholder: `${min}`,
              sx: priceInputSx,
              type: 'number',
              value: minPriceRaw,
            }}
          />
          <Box component="span" sx={priceSeparatorSx}>
            -
          </Box>
          <ControlledTextField
            control={control}
            name="maxPrice"
            stackProps={{ width: 'auto' }}
            textFieldProps={{
              variant: 'standard',
              id: `${id}.maxPrice`,
              inputProps: { max, min, step },
              InputProps: { endAdornment: euroAdornment },
              onChange: handleMaxChange,
              placeholder: `${max}`,
              sx: priceInputSx,
              type: 'number',
              value: maxPriceRaw,
            }}
          />
        </Box>
      </Box>

      <Box sx={sliderWrapperSx}>
        <ControlledRangeSlider
          control={control}
          controllerOptions={{ defaultValue: undefined }}
          max={max}
          maxName="maxPrice"
          min={min}
          name="minPrice"
          rangeSliderProps={{ handles: [minPrice, maxPrice] }}
          step={step}
        />
      </Box>

      {liveEdit ? null : (
        <Box sx={buttonsWrapperSx}>
          <Box component="button" onClick={onClear} sx={clearButtonSx} type="button">
            {labels.clear}
          </Box>
          <Box component="button" onClick={handleCancel} sx={cancelButtonSx} type="button">
            {labels.cancel}
          </Box>
          <Box component="button" sx={submitButtonSx} type="submit">
            {labels.submit}
          </Box>
        </Box>
      )}
    </Box>
  );
};

PriceFilterFormComponent.displayName = 'PriceFilterForm';

const PriceFilterForm = memo(PriceFilterFormComponent);

export default PriceFilterForm;
